// Connect — 4-provider CRM connect flow + status display + reconnect.
//
// Providers:
//   HubSpot    — OAuth (POST /v1/oauth/hubspot/start → redirect)
//   Pipedrive  — OAuth (POST /v1/oauth/pipedrive/start → redirect)
//   Zoho CRM   — OAuth (POST /v1/oauth/zoho_crm/start → redirect)
//   Freshsales — API key form (POST /v1/oauth/freshsales/connect-key)
//                body: { key: <api_key>, extra: { base_url: "https://<subdomain>.freshsales.io" } }
//
// Also renders the upsell banner when show_hubspot_upsell_banner=true.

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@avagency/frontend/components/ui/card';
import { Button } from '@avagency/frontend/components/ui/button';
import { Skeleton } from '@avagency/frontend/components/ui/skeleton';
import { Plug, CheckCircle, AlertCircle, RefreshCw, Gift } from 'lucide-react';

import {
  fetchIntegrations,
  fetchBillingStatus,
  startHubSpotOAuth,
  startPipedriveOAuth,
  startZohoOAuth,
  connectFreshsalesByKey,
  disconnectIntegration,
  type IntegrationRef,
  type Me,
  type OAuthCallbackStatus,
} from '../api';

// OAuth provider type: start fn returns void (navigates directly to authorize_url).
type OAuthStartFn = () => Promise<void>;

interface ProviderCardProps {
  name: string;
  description: string;
  integration?: IntegrationRef;
  onConnect: OAuthStartFn;
  onDisconnect?: (id: string) => void;
  connecting: boolean;
}

function ProviderCard({
  name,
  description,
  integration,
  onConnect,
  onDisconnect,
  connecting,
}: ProviderCardProps) {
  const isActive = integration?.status === 'active';
  const isReauth = integration?.status === 'reauth_required';

  return (
    <Card className={`transition-colors ${isActive ? 'border-green-500/30' : isReauth ? 'border-orange-500/30' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">{name}</CardTitle>
          {isActive && (
            <span className="flex items-center gap-1.5 text-xs text-green-400">
              <CheckCircle size={13} /> Connected
            </span>
          )}
          {isReauth && (
            <span className="flex items-center gap-1.5 text-xs text-orange-400">
              <AlertCircle size={13} /> Reconnect needed
            </span>
          )}
        </div>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0 flex items-center gap-2">
        {!integration || isReauth ? (
          <Button size="sm" onClick={onConnect} disabled={connecting} className="gap-1.5">
            <Plug size={13} />
            {connecting ? 'Connecting…' : isReauth ? 'Reconnect' : 'Connect'}
          </Button>
        ) : null}
        {isActive && integration && onDisconnect && (
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground hover:text-destructive text-xs"
            onClick={() => onDisconnect(integration.id)}
          >
            Disconnect
          </Button>
        )}
        {isActive && integration && (
          <span className="text-xs text-muted-foreground truncate">
            {integration.display_name || integration.external_id}
          </span>
        )}
      </CardContent>
    </Card>
  );
}

// Freshsales requires an API key + subdomain, not OAuth.
interface FreshsalesFormProps {
  onSubmit: (apiKey: string, domain: string) => void;
  submitting: boolean;
  error?: string;
}

function FreshsalesForm({ onSubmit, submitting, error }: FreshsalesFormProps) {
  const [apiKey, setApiKey] = useState('');
  const [domain, setDomain] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKey.trim() || !domain.trim()) return;
    // Normalize: strip any https:// prefix or .freshsales.io suffix the user may paste.
    let subdomain = domain.trim();
    subdomain = subdomain.replace(/^https?:\/\//, '');
    subdomain = subdomain.replace(/\.freshsales\.io.*$/, '');
    onSubmit(apiKey.trim(), subdomain);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 mt-2">
      <div>
        <label className="block text-xs text-muted-foreground mb-1">Freshsales API Key</label>
        <input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="Your Freshsales API key"
          required
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>
      <div>
        <label className="block text-xs text-muted-foreground mb-1">Subdomain</label>
        <div className="flex items-center">
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="yourcompany"
            required
            className="flex-1 rounded-l-md border border-r-0 border-border bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <span className="rounded-r-md border border-border bg-muted px-3 py-1.5 text-sm text-muted-foreground">
            .freshsales.io
          </span>
        </div>
      </div>
      {error && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle size={11} /> {error}
        </p>
      )}
      <Button type="submit" size="sm" disabled={submitting || !apiKey.trim() || !domain.trim()}>
        {submitting ? 'Connecting…' : 'Connect Freshsales'}
      </Button>
    </form>
  );
}

export interface ConnectProps {
  session: Me;
  callbackStatus?: OAuthCallbackStatus;
  callbackError?: string | null;
  reauthRequired?: boolean;
}

export function Connect({ callbackStatus, callbackError, reauthRequired }: ConnectProps) {
  const qc = useQueryClient();
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null);
  const [oauthError, setOauthError] = useState<string | undefined>();
  const [freshsalesError, setFreshsalesError] = useState<string | undefined>();

  const integrations = useQuery({
    queryKey: ['pipeline-rot', 'integrations'],
    queryFn: fetchIntegrations,
  });

  const billing = useQuery({
    queryKey: ['pipeline-rot', 'billing-status'],
    queryFn: fetchBillingStatus,
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectIntegration,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipeline-rot', 'integrations'] }),
  });

  const freshsalesMutation = useMutation({
    mutationFn: ({ apiKey, domain }: { apiKey: string; domain: string }) =>
      connectFreshsalesByKey(apiKey, domain),
    onSuccess: () => {
      setFreshsalesError(undefined);
      qc.invalidateQueries({ queryKey: ['pipeline-rot', 'integrations'] });
    },
    onError: (err: Error & { status?: number }) => {
      setFreshsalesError(err.message || 'Connection failed. Check your API key.');
    },
  });

  const byProvider = (provider: string): IntegrationRef | undefined =>
    integrations.data?.integrations.find((i) => i.provider === provider);

  // oauthConnect — sets loading state, calls the start fn (which navigates to
  // authorize_url on success). On error, clears loading and surfaces error to user.
  const oauthConnect = async (provider: string, startFn: OAuthStartFn) => {
    setConnectingProvider(provider);
    setOauthError(undefined);
    try {
      await startFn();
      // startFn navigates away on success; if we reach here it's a no-op redirect.
    } catch (err) {
      setConnectingProvider(null);
      setOauthError((err as Error).message || 'Failed to start OAuth. Please try again.');
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <div className="flex items-center gap-3">
        <Plug size={20} className="text-muted-foreground" />
        <div>
          <h1 className="text-lg font-semibold text-foreground">Connect CRM</h1>
          <p className="text-sm text-muted-foreground">
            Connect your CRM to start scanning for pipeline rot. Up to 4 integrations (one per provider).
          </p>
        </div>
      </div>

      {/* OAuth callback status banner */}
      {callbackStatus === 'success' && (
        <div className="flex items-center gap-2 rounded-lg border border-green-500/30 bg-green-500/[0.08] px-4 py-3 text-sm text-green-400">
          <CheckCircle size={15} /> CRM connected successfully.
        </div>
      )}
      {callbackStatus === 'error' && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/[0.08] px-4 py-3 text-sm text-destructive">
          <AlertCircle size={15} />
          {callbackError
            ? `Connection failed: ${callbackError}`
            : 'OAuth connection failed. Please try again.'}
        </div>
      )}
      {reauthRequired && callbackStatus === null && (
        <div className="flex items-center gap-2 rounded-lg border border-orange-500/30 bg-orange-500/[0.08] px-4 py-3 text-sm text-orange-400">
          <RefreshCw size={15} /> One of your CRM connections needs to be re-authorized. Use the Reconnect button below.
        </div>
      )}
      {oauthError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/[0.08] px-4 py-3 text-sm text-destructive">
          <AlertCircle size={14} /> {oauthError}
        </div>
      )}

      {/* HubSpot upsell banner — show to free tenants with HubSpot active */}
      {billing.data?.show_hubspot_upsell_banner && (
        <div className="flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/[0.06] px-4 py-4">
          <Gift size={18} className="text-primary mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-semibold text-foreground mb-0.5">
              Bundle Pipeline Rot + HubSpot Guardian for $59/mo
            </div>
            <div className="text-xs text-muted-foreground mb-3">
              Save $19/mo vs buying separately. Includes full alert dispatch + HubSpot contact-cap monitoring.
            </div>
            <Button size="sm" className="gap-1.5" asChild>
              <a href="/billing">Upgrade to bundle</a>
            </Button>
          </div>
        </div>
      )}

      {/* Integrations loading */}
      {integrations.isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      )}

      {/* Integrations error — show retry, not broken provider cards */}
      {integrations.isError && (
        <div className="space-y-2">
          <div className="rounded-lg border border-destructive/30 bg-destructive/[0.08] px-4 py-3 text-sm text-destructive flex items-center gap-2">
            <AlertCircle size={14} />
            Failed to load integrations. {(integrations.error as Error).message}
          </div>
          <button onClick={() => integrations.refetch()} className="text-xs text-primary underline">
            Retry
          </button>
        </div>
      )}

      {/* Provider cards — only when we have a valid integrations response */}
      {integrations.data && !integrations.isError && (
        <div className="grid gap-4">
          <ProviderCard
            name="HubSpot"
            description="Connects via OAuth. Scans deals in your HubSpot CRM pipeline."
            integration={byProvider('hubspot')}
            onConnect={() => oauthConnect('hubspot', startHubSpotOAuth)}
            onDisconnect={(id) => disconnectMutation.mutate(id)}
            connecting={connectingProvider === 'hubspot'}
          />
          <ProviderCard
            name="Pipedrive"
            description="Connects via OAuth. Scans deals in your Pipedrive pipeline."
            integration={byProvider('pipedrive')}
            onConnect={() => oauthConnect('pipedrive', startPipedriveOAuth)}
            onDisconnect={(id) => disconnectMutation.mutate(id)}
            connecting={connectingProvider === 'pipedrive'}
          />
          <ProviderCard
            name="Zoho CRM"
            description="Connects via OAuth. Scans deals in your Zoho CRM pipeline."
            integration={byProvider('zoho_crm')}
            onConnect={() => oauthConnect('zoho_crm', startZohoOAuth)}
            onDisconnect={(id) => disconnectMutation.mutate(id)}
            connecting={connectingProvider === 'zoho_crm'}
          />

          {/* Freshsales — key-based connect */}
          <Card className={byProvider('freshsales')?.status === 'active' ? 'border-green-500/30' : ''}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">Freshsales</CardTitle>
                {byProvider('freshsales')?.status === 'active' && (
                  <span className="flex items-center gap-1.5 text-xs text-green-400">
                    <CheckCircle size={13} /> Connected
                  </span>
                )}
              </div>
              <CardDescription className="text-xs">
                Connects via API key. Enter your Freshsales API key and subdomain.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              {byProvider('freshsales')?.status === 'active' ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {byProvider('freshsales')?.display_name || byProvider('freshsales')?.external_id}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground hover:text-destructive text-xs"
                    onClick={() => {
                      const id = byProvider('freshsales')?.id;
                      if (id) disconnectMutation.mutate(id);
                    }}
                  >
                    Disconnect
                  </Button>
                </div>
              ) : (
                <FreshsalesForm
                  onSubmit={(apiKey, domain) => freshsalesMutation.mutate({ apiKey, domain })}
                  submitting={freshsalesMutation.isPending}
                  error={freshsalesError}
                />
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
